/* eslint no-shadow: ["error", { "allow": ["state"] }] */
/* eslint arrow-body-style: "off" */
import _ from 'lodash';
import qs from 'qs';
import axios from 'axios';
import { differenceInMilliseconds, getTime } from 'date-fns';

// mockup
// import {
//   bandwidthEstimatePlayback,
//   bandwidthEstimatePlaybackDetail,
//   bufferPlayback,
//   bufferPlaybackDetail,
//   bufferAndSeekPlayback,
//   bufferAndSeekPlaybackDetail,
//   bufferHealthCheckPlayback,
//   bufferHealthCheckPlaybackDetail,
//   errorPlayback,
//   errorPlaybackDetail
// } from '../mockup';

const state = {
  currentPlaybackId: null,
  currentPlayback: null,
  firstEvent: {
    client_timestamp: null,
  },
  lastEvent: {
    client_timestamp: null,
  },
  eventsById: {},
  eventIds: [],
  eventsIdGroupKey: {},
  eventsByMetric: {
    playback_resolution: [],
    bandwidth_estimate: [],
    buffer_health: [],
    response_time: [],
  },
  playbackDuration: null,
  stateEventList: {
    isPreLoaderShowing: true,
    isLoading: true,
    isError: false,
    error: null,
    height: '500',
    currentScrollId: null,
  },
};

const getters = {
  currentPlayback: state => state.currentPlayback,
  currentPlaybackId: state => state.currentPlaybackId,
  eventIds: state => state.eventIds,
  getEventById: state => eventId => state.eventsById[eventId],
  eventsIdGroupKey: state => state.eventsIdGroupKey,
  eventsByMetric: state => state.eventsByMetric,
  firstEvent: state => state.firstEvent,
  lastEvent: state => state.lastEvent,
  playbackDuration: state => state.playbackDuration,
  stateEventList: state => state.stateEventList,
};

const getWidthByTime = (event, nextEvent, timePerPercent) => {
  if (event && nextEvent) {
    const width = ((nextEvent.client_timestamp - event.client_timestamp) / timePerPercent) * 100;
    return width < 1 ? '2px' : `${width}%`;
  }
  return '2px';
};

const getPlaybackEvents = (projectId, filter) => {
  const query = qs.stringify(filter, { addQueryPrefix: true, skipNulls: true });
  return axios.get(`/projects/${projectId}/clients/playbacks${query}`);
};

const getPlaybackDuration = (startWatchingTime, endWatchingTime) => {
  if (startWatchingTime && endWatchingTime) {
    return differenceInMilliseconds(getTime(endWatchingTime), getTime(startWatchingTime));
  }
  return null;
};

const groupEventByKey = (eventsGroupKey, typeOfStart, typeOfStop) => {
  return _.sortBy(_.union(eventsGroupKey[typeOfStart],
    eventsGroupKey[typeOfStop]), 'client_timestamp');
};

const mutations = {
  setEvents(state, events) {
    const newEventsById = _.chain(events)
      .mapKeys(event => event.event_id)
      .value();
    // update store
    Object.assign(state.eventsById, { ...newEventsById });

    // update store
    const newEventIds = _.map(events, event => event.event_id);
    state.eventIds = [...state.eventIds, ...newEventIds];
  },
  setGroupEventsByKey(state, events) {
    const eventsGroupKey = _.groupBy(events, 'type');

    // group of buffering
    const buffering = groupEventByKey(eventsGroupKey, 'playback.buffering_start', 'playback.buffering_stop');

    buffering.forEach((eventItem, index) => {
      const width = getWidthByTime(eventItem, buffering[index + 1], state.playbackDuration);
      state.eventsById[eventItem.event_id] = { ...state.eventsById[eventItem.event_id], width };
    });

    // group of seeking
    const seeking = groupEventByKey(eventsGroupKey, 'playback.seeking_start', 'playback.seeking_stop');

    seeking.forEach((eventItem, index) => {
      const width = getWidthByTime(eventItem, seeking[index + 1], state.playbackDuration);
      state.eventsById[eventItem.event_id] = { ...state.eventsById[eventItem.event_id], width };
    });

    Object.assign(eventsGroupKey, { buffering, seeking });

    const newEventsIdGroupKey = _.chain(eventsGroupKey)
      .mapValues(group => _.map(group, item => item.event_id))
      .value();

    if (!_.isEmpty(state.eventsIdGroupKey)) {
      // update store
      _.forEach(newEventsIdGroupKey, (value, key) => {
        state.eventsIdGroupKey[key].concat(value);
      });
    } else {
      // set store
      state.eventsIdGroupKey = _.chain(eventsGroupKey)
        .mapValues(group => _.map(group, item => item.event_id))
        .value();
    }
  },
  setEventsByMetric(state, events) {
    const resolution = [];
    const bandwidthEstimate = [];
    const bufferHealth = [];
    const responseTime = [];

    _.forEach(events, (value) => {
      resolution.push(value.playback_resolution || null);
      bandwidthEstimate.push(value.bandwidth_estimate || null);
      bufferHealth.push(value.buffer_health || null);
      responseTime.push(value.response_time || null);
    });

    state.eventsByMetric.playback_resolution = [...state.eventsByMetric.playback_resolution, ...resolution];
    state.eventsByMetric.bandwidth_estimate = [...state.eventsByMetric.bandwidth_estimate, ...bandwidthEstimate];
    state.eventsByMetric.buffer_health = [...state.eventsByMetric.buffer_health, ...bufferHealth];
    state.eventsByMetric.response_time = [...state.eventsByMetric.response_time, ...responseTime];
  },
  setPlaybackDuration(state) {
    const startWatchingTime = new Date(state.currentPlayback.start_watching_time);
    const endWatchingTime = new Date(state.currentPlayback.end_watching_time);
    state.playbackDuration = getPlaybackDuration(startWatchingTime, endWatchingTime);
  },
  setFirstEvent(state, firstEvent) {
    state.firstEvent = firstEvent;
  },
  setLastEvent(state, lastEvent) {
    state.lastEvent = lastEvent;
  },
  setCurrentPlaybackId(state, currentPlaybackId) {
    state.currentPlaybackId = currentPlaybackId;
  },
  setStateMessage(state, message) {
    switch (message.type) {
      case 'loading':
        state.stateEventList.isLoading = false;
        state.stateEventList.error = null;
        break;
      case 'success':
        state.stateEventList.currentScrollId = message.payload.scrollId;
        state.stateEventList.isPreLoaderShowing = false;
        state.stateEventList.isLoading = false;
        state.stateEventList.isError = false;
        break;
      case 'error':
        state.stateEventList.isPreLoaderShowing = false;
        state.stateEventList.isError = true;
        state.stateEventList.isLoading = false;
        state.stateEventList.error = message.payload;
        break;
      default:
        state.stateEventList.isPreLoaderShowing = true;
        state.stateEventList.isLoading = false;
        state.stateEventList.isError = false;
        state.stateEventList.error = null;
        state.stateEventList.currentScrollId = null;
        break;
    }
  },
  setCurrentPlayback(state, playbackDetail) {
    state.currentPlayback = playbackDetail;
  },
  destoryEvents(state) {
    state.currentPlaybackId = null;
    state.currentPlayback = null;
    state.currentEventPeriod = {
      start: null,
      end: null,
    };
    state.firstEvent = { client_timestamp: null };
    state.lastEvent = { client_timestamp: null };
    state.eventsById = {};
    state.eventIds = [];
    state.eventsIdGroupKey = {};
    state.eventsByMetric = {
      playback_resolution: [],
      bandwidth_estimate: [],
      buffer_health: [],
      response_time: [],
    };
    state.playbackDuration = null;
    state.stateEventList = {
      isPreLoaderShowing: true,
      isLoading: true,
      isError: false,
      error: null,
      height: '500',
      currentScrollId: null,
    };
  },
};

const actions = {
  async loadEvents({ dispatch, commit, rootGetters }, filter) {
    try {
      commit('setStateMessage', { type: 'loading' });
      const projectId = rootGetters.currentProjectId;
      const response = await getPlaybackEvents(projectId, filter);
      const newEvent = _.map(response.data.playbacks.datas, item => Object.freeze(item));

      if (state.eventIds && state.eventIds.length === 0) {
        await dispatch('setEvents', newEvent);
      } else {
        await dispatch('updateEvents', newEvent);
      }
      commit('setStateMessage', { type: 'success', payload: response.data.playbacks });
    } catch (error) {
      commit('setStateMessage', { type: 'error', payload: error.response });
    }
  },
  setCurrentPlayback({ commit }, playbackDetail) {
    commit('setCurrentPlayback', playbackDetail);
  },
  setEvents({ commit }, events) {
    commit('setFirstEvent', _.head(events));
    commit('setLastEvent', _.last(events));
    commit('setPlaybackDuration');
    commit('setEvents', events);
    commit('setGroupEventsByKey', events);
    commit('setEventsByMetric', events);
  },
  updateEvents({ commit }, newEvents) {
    commit('setLastEvent', _.last(newEvents));
    commit('setEvents', newEvents);
    commit('setGroupEventsByKey', newEvents);
    commit('setEventsByMetric', newEvents);
  },
  setCurrentPlaybackId({ commit }, currentPlaybackId) {
    commit('setCurrentPlaybackId', currentPlaybackId);
  },
  destoryEvents({ commit }) {
    commit('destoryEvents');
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
